<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Rapport par Produit</h1>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Filters -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-3">
          <v-row align="center" dense>
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="stores"
                item-value="id"
                item-text="name"
                placeholder="Emplacement"
                v-model="store_id"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-select
                :items="categories"
                item-value="id"
                item-text="name"
                placeholder="Catégorie"
                v-model="category_id"
                outlined
                dense
                hide-details
                clearable
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="auto">
              <div class="d-flex gap-2">
                <v-btn color="primary" depressed small @click="filter" :loading="loading" class="mr-1">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn outlined small @click="reset" :loading="loading" class="mr-1">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="error" outlined small @click="exportPDF" class="mr-1">
                  <v-icon small>mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn color="success" outlined small @click="exportExcel">
                  <v-icon small>mdi-file-excel</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Products table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table" id="content">
            <thead>
              <tr>
                <th class="text-left">PRODUIT</th>
                <th class="text-left">CATÉGORIE</th>
                <th class="text-center">QTÉ</th>
                <th class="text-center" style="width:60px"></th>
                <th class="text-right">COÛT</th>
                <th class="text-right">VENTES</th>
                <th class="text-right">PROFIT</th>
                <th class="text-center">MARGE</th>
              </tr>
              <tr class="totals-row">
                <th class="text-left" colspan="2">TOTAL ({{ sortedRows.length }} produits)</th>
                <th class="text-center">{{ grandTotals.quantity }}</th>
                <th></th>
                <th class="text-right">{{ grandTotals.cost | formatNumber }}</th>
                <th class="text-right">{{ grandTotals.sales | formatNumber }}</th>
                <th class="text-right">{{ grandTotals.profit | formatNumber }}</th>
                <th class="text-center">{{ grandTotals.margin }}%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in sortedRows" :key="idx">
                <td class="text-left font-weight-medium">{{ p.product_name }}</td>
                <td class="text-left text-caption text--secondary">{{ p.category_name }}</td>
                <td class="text-center font-weight-bold">{{ p.quantity }}</td>
                <td class="text-center">
                  <span v-if="p.qtyDelta > 0" class="trend-up">
                    <v-icon x-small color="success">mdi-arrow-up</v-icon>
                    <span class="success--text">{{ p.qtyDelta }}</span>
                  </span>
                  <span v-else-if="p.qtyDelta < 0" class="trend-down">
                    <v-icon x-small color="error">mdi-arrow-down</v-icon>
                    <span class="error--text">{{ Math.abs(p.qtyDelta) }}</span>
                  </span>
                  <span v-else class="text--disabled">—</span>
                </td>
                <td class="text-right">{{ p.cost | formatNumber }}</td>
                <td class="text-right">{{ p.sales | formatNumber }}</td>
                <td class="text-right" :class="p.profit >= 0 ? 'success--text' : 'error--text'">
                  {{ p.profit | formatNumber }}
                </td>
                <td class="text-center">
                  <v-chip x-small :color="p.marginPct >= 30 ? 'success' : (p.marginPct >= 10 ? 'warning' : 'error')" dark>
                    {{ p.marginPct }}%
                  </v-chip>
                </td>
              </tr>
              <tr v-if="sortedRows.length === 0">
                <td colspan="8" class="text-center text--secondary pa-6">
                  Aucune donnée pour cette période
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import moment from 'moment'

export default {
  name: 'ProductsReportView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Rapport Produits', disabled: false, href: '/productsreport' }],
    rows: [],
    prevRows: [],
    categories: [],
    category_id: '',
    enable: false,
    loading: false,
    filter_rate_id: 1,
    store_id: '',
    business_id: 0,
    stores: [],
  }),

  computed: {
    currency() {
      return this.filter_rate_id == 1 ? 'HTG' : 'USD'
    },

    sortedRows() {
      var self = this
      var rateKey = self.filter_rate_id == 1 ? 'HTG' : 'USD'

      // Build previous period lookup
      var prevMap = {}
      if (self.prevRows) {
        Object.keys(self.prevRows).forEach(function (key) {
          var p = self.prevRows[key]
          prevMap[p.product_id || p.product_name] = parseFloat(p.quantity) || 0
        })
      }

      // Map to flat array with computed fields
      var arr = []
      Object.keys(self.rows).forEach(function (key) {
        var r = self.rows[key]
        var turnover = r.turnover && r.turnover[rateKey] ? r.turnover[rateKey] : { cost: 0, total: 0 }
        var cost = parseFloat(turnover.cost) || 0
        var sales = parseFloat(turnover.total) || 0
        var profit = sales - cost
        var marginPct = sales > 0 ? Math.round((profit / sales) * 100) : 0
        var qty = parseFloat(r.quantity) || 0
        var prevQty = prevMap[r.product_id || r.product_name] || 0

        arr.push({
          product_name: r.product_name,
          category_name: r.category_name || '—',
          quantity: qty,
          qtyDelta: qty - prevQty,
          cost: cost,
          sales: sales,
          profit: profit,
          marginPct: marginPct,
        })
      })

      // Sort by quantity desc (most sold at top)
      arr.sort(function (a, b) { return b.quantity - a.quantity })
      return arr
    },

    grandTotals() {
      var totals = { quantity: 0, cost: 0, sales: 0, profit: 0, margin: 0 }
      this.sortedRows.forEach(function (r) {
        totals.quantity += r.quantity
        totals.cost += r.cost
        totals.sales += r.sales
        totals.profit += r.profit
      })
      totals.margin = totals.sales > 0 ? Math.round((totals.profit / totals.sales) * 100) : 0
      return totals
    },
  },

  methods: {
    getDateParams() {
      var isValid = function (val) { return val && val !== 'null' && val.trim() !== '' }
      var from = isValid(localStorage.debut_periode) ? localStorage.debut_periode : moment().format('YYYY-MM-DD')
      var to = isValid(localStorage.fin_periode) ? localStorage.fin_periode : moment().format('YYYY-MM-DD')
      return { from: from, to: to }
    },

    getPrevDateParams() {
      var dates = this.getDateParams()
      var fromDate = moment(dates.from)
      var toDate = moment(dates.to)
      var days = toDate.diff(fromDate, 'days') + 1
      var prevTo = fromDate.clone().subtract(1, 'day').format('YYYY-MM-DD')
      var prevFrom = fromDate.clone().subtract(days, 'days').format('YYYY-MM-DD')
      return { from: prevFrom, to: prevTo }
    },

    buildFilterParams(dates) {
      var params = 'from=' + dates.from + '&to=' + dates.to
      if (this.store_id) params += '&store=' + this.store_id
      if (this.category_id) params += '&category=' + this.category_id
      return params
    },

    load() {
      var self = this
      var dates = this.getDateParams()
      var prevDates = this.getPrevDateParams()

      // Current period
      this.$axios.get('/reports/products?' + this.buildFilterParams(dates))
        .then(function (response) {
          self.rows = response.data.result || []

          // Previous period (for comparison)
          self.$axios.get('/reports/products?' + self.buildFilterParams(prevDates))
            .then(function (prevResponse) {
              self.prevRows = prevResponse.data.result || []
              self.enable = true
            })
            .catch(function () {
              self.prevRows = []
              self.enable = true
            })
        })
        .catch(function () {
          self.enable = true
        })
    },

    filter() {
      this.enable = false
      this.load()
    },

    reset() {
      this.store_id = ''
      this.category_id = ''
      this.filter()
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297
      var ph = 210
      var ml = 12
      var mr = pw - 12
      var cw = mr - ml
      var biz = {}
      try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }

      var primary = [25, 65, 115]
      var dark = [30, 41, 59]
      var muted = [100, 116, 139]
      var light = [248, 250, 252]
      var border = [220, 225, 230]
      var dates = self.getDateParams()

      // Header
      doc.setFillColor(...primary)
      doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(255, 255, 255)
      doc.text('RAPPORT PAR PRODUIT', ml, 11)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(200, 215, 235)
      doc.text(dates.from + ' — ' + dates.to, mr, 8, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 13, { align: 'right' })

      var y = 24

      // Columns
      var cols = [
        { label: 'PRODUIT', w: 55, align: 'left' },
        { label: 'CATÉGORIE', w: 35, align: 'left' },
        { label: 'QTÉ', w: 16, align: 'center' },
        { label: 'COÛT (' + self.currency + ')', w: 28, align: 'right' },
        { label: 'VENTES (' + self.currency + ')', w: 28, align: 'right' },
        { label: 'PROFIT (' + self.currency + ')', w: 28, align: 'right' },
        { label: 'MARGE', w: 16, align: 'center' },
      ]

      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW
      cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0)
      cols[0].w += (cw - actualW)

      var cx = ml
      cols.forEach(function (c) { c.x = cx; cx += c.w })

      var rowH = 6.5

      function fmt(v) {
        if (v === null || v === undefined || isNaN(v)) return '0.00'
        var parts = Math.abs(parseFloat(v)).toFixed(2).split('.')
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        return (parseFloat(v) < 0 ? '-' : '') + parts.join('.')
      }

      function drawHeader() {
        doc.setFillColor(...primary)
        doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          doc.text(c.label, tx, y + 4.5, { align: c.align })
        })
        y += rowH + 1
      }

      drawHeader()

      self.sortedRows.forEach(function (row, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }

        doc.setDrawColor(...border)
        doc.setLineWidth(0.1)
        doc.line(ml, y + rowH, mr, y + rowH)

        doc.setFontSize(7)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(...dark)

        var name = row.product_name.length > 28 ? row.product_name.substring(0, 26) + '..' : row.product_name
        var cat = row.category_name.length > 18 ? row.category_name.substring(0, 16) + '..' : row.category_name

        var vals = [name, cat, String(row.quantity), fmt(row.cost), fmt(row.sales), fmt(row.profit), row.marginPct + '%']

        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          if (i === 5 && row.profit < 0) doc.setTextColor(220, 38, 38)
          else doc.setTextColor(...dark)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      // Totals
      y += 1
      doc.setFillColor(...light)
      doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(...primary)
      doc.setLineWidth(0.4)
      doc.line(ml, y, mr, y)
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(...dark)
      var gt = self.grandTotals
      doc.text('TOTAL (' + self.sortedRows.length + ' produits)', ml + 3, y + 4.8)
      doc.text(String(gt.quantity), cols[2].x + cols[2].w / 2, y + 4.8, { align: 'center' })
      doc.text(fmt(gt.cost), cols[3].x + cols[3].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(gt.sales), cols[4].x + cols[4].w - 3, y + 4.8, { align: 'right' })
      doc.text(fmt(gt.profit), cols[5].x + cols[5].w - 3, y + 4.8, { align: 'right' })
      doc.text(gt.margin + '%', cols[6].x + cols[6].w / 2, y + 4.8, { align: 'center' })

      // Footer
      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6)
        doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted)
        doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date()
        doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }

      doc.save('rapport_produits.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}
      try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var dates = self.getDateParams()

      var wsData = []
      wsData.push(['RAPPORT PAR PRODUIT'])
      wsData.push([biz.name || ''])
      wsData.push(['Du ' + dates.from + ' au ' + dates.to])
      wsData.push([])

      var headers = ['Produit', 'Catégorie', 'Quantité', 'Coût (' + self.currency + ')', 'Ventes (' + self.currency + ')', 'Profit (' + self.currency + ')', 'Marge (%)']
      wsData.push(headers)

      self.sortedRows.forEach(function (r) {
        wsData.push([
          r.product_name,
          r.category_name,
          r.quantity,
          Math.round(r.cost * 100) / 100,
          Math.round(r.sales * 100) / 100,
          Math.round(r.profit * 100) / 100,
          r.marginPct,
        ])
      })

      var gt = self.grandTotals
      wsData.push([])
      wsData.push([
        'TOTAL (' + self.sortedRows.length + ' produits)', '',
        gt.quantity,
        Math.round(gt.cost * 100) / 100,
        Math.round(gt.sales * 100) / 100,
        Math.round(gt.profit * 100) / 100,
        gt.margin,
      ])

      var wb = XLSX.utils.book_new()
      var ws = XLSX.utils.aoa_to_sheet(wsData)

      ws['!cols'] = [
        { wch: 30 }, { wch: 18 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 10 },
      ]

      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: headers.length - 1 } }) }
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } },
      ]

      // Number format for amount columns
      for (var r = 5; r < wsData.length; r++) {
        for (var c = 3; c <= 5; c++) {
          var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })]
          if (cell && typeof cell.v === 'number') { cell.t = 'n'; cell.z = '#,##0.00' }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Produits')
      XLSX.writeFile(wb, 'rapport_produits.xlsx')
    },

    async getStores() {
      this.business_id = JSON.parse(localStorage.business).id
      var self = this
      await this.$axios.get('/stores?orderasc=name&business_id=' + this.business_id)
        .then(function (response) { self.stores = response.data })
    },

    async getCategories() {
      var self = this
      await this.$axios.get('/categories?orderasc=name')
        .then(function (response) { self.categories = response.data })
    },
  },

  created() {
    this.filter_rate_id = localStorage.filter_rate_id
    this.load()
    this.getCategories()
    this.getStores()
  },
}
</script>

<style scoped>
.filter-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}

.table-responsive {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.report-table thead th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  padding: 12px;
  border-bottom: 2px solid #e8ecf1;
  white-space: nowrap;
  z-index: 1;
}

.report-table .totals-row th {
  background: #f1f5f9;
  color: #1a2332;
  font-weight: 700;
  font-size: 0.75rem;
  border-bottom: 2px solid #cbd5e1;
}

.report-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.report-table tbody tr:hover {
  background-color: rgba(25, 65, 115, 0.03);
}

.trend-up, .trend-down {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.gap-2 {
  gap: 4px;
}
</style>
